import { useState, useEffect } from 'react';
import { createStage } from '../gameHelpers';
import { PlayerType } from './usePlayer';
import { StageType } from '../types';

export const useStage = (player: PlayerType, resetPlayer: () => void) => {
  const [stage, setStage] = useState(createStage());
  const [rowsCleared, setRowsCleared] = useState(0);

  useEffect(() => {
    setRowsCleared(0);

    const sweepRows = (newStage: StageType): StageType => {
      return newStage.reduce((acc, row) => {
        if (row.findIndex(cell => cell[0] === 0) === -1) {
          setRowsCleared(prev => prev + 1);
          acc.unshift(new Array(newStage[0].length).fill([0, 'clear']) as [string | number, string][]);
          return acc;
        }
        acc.push(row);
        return acc;
      }, [] as StageType);
    };

    const updateStage = (prevStage: StageType): StageType => {
     // First flush the stage, but keep merged cells
      const newStage = prevStage.map(row =>
        row.map(cell => (cell[1] === 'clear' ? [0, 'clear'] : cell))
      );

      // Then draw the tetromino
      player.tetromino.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value !== 0) {
            // Add boundary check to prevent accessing undefined elements
            const yPos = y + player.pos.y;
            const xPos = x + player.pos.x;
            
            if (
              newStage[yPos] && 
              newStage[yPos][xPos] !== undefined
            ) {
             if (newStage[yPos][xPos][1] !== 'merged') {
                newStage[yPos][xPos] = [
                  value,
                  `${player.collided ? 'merged' : 'clear'}`,
                ] as [string | number, string];
              }
            }
          }
        });
      });

      // Then check if we collided
      if (player.collided) {
        resetPlayer();
        return sweepRows(newStage as StageType);
      }

      return newStage as StageType;
    };

    setStage(prev => updateStage(prev));
  }, [player, resetPlayer]);

  return [stage, setStage, rowsCleared] as const;
};
